// UI text (English only since 2026-10-19 - the user had the Chinese interface removed). Components
// read it through useT() (see i18n.js).
export const TRANSLATIONS = {
  en: {
    locale: 'en-US',
    pageTitle: 'Stock Symbol Maintenance',
    detailTitle: 'Company Info',
    subtitle: 'company_profiles',
    backToList: '← Back to Stock List',
    tabList: 'Stock List',
    tabForm: 'Add Stock',
    tabEdit: 'Update Data',
    tabMonitor: 'Stock Monitor',
    labelSymbol: 'Stock Symbol',
    labelCompany: 'Company Name',
    labelMarket: 'Trading Market',
    labelCategory: 'Category',
    labelDelisted: 'Delisted',
    labelCusips: 'CUSIP',
    labelSector: 'Sector',
    labelIndustry: 'Industry',
    labelCurrency: 'Currency',
    labelLocation: 'Company Location',
    labelUrl: 'Website',
    labelCeo: 'CEO',
    labelDescription: 'Description',
    labelUpdated: 'Updated At',
    listed: 'Listed',
    delisted: 'Delisted',
    yes: 'Yes',
    no: 'No',
    searchPlaceholder: 'Search symbol or company name',
    allMarkets: 'All Markets',
    allStatuses: 'All statuses',
    statusListed: 'Listed',
    statusDelisted: 'Delisted',
    exportCsvBtn: 'Export CSV',
    emptyState: 'No data yet',
    loading: 'Loading…',
    prevPage: 'Previous',
    nextPage: 'Next',
    pageIndicator: (page, totalPages, total) => `Page ${page} of ${totalPages} (${total.toLocaleString('en-US')} stocks)`,
    addTitle: 'Add Stock',
    addHelp: "Enter a stock symbol and the company name and market are filled in from NASDAQ Trader and Yahoo. After the add, the server looks up sector, address, website, CEO, description and CUSIP right away.",
    addBtn: 'Add',
    operationFailed: 'Operation failed',
    duplicateSymbol: (symbol) => `Stock symbol ${symbol} already exists and cannot be added again. View: `,
    symbolInUse: (symbol) => `Stock symbol ${symbol} is already used by another live row, so this stock can't be changed to it. View: `,
    symbolNameTaken: (symbol, delisted) => `Another${delisted ? ' (delisted)' : ''} row already has stock symbol ${symbol} with this company name (the table doesn't allow two rows with the same symbol and name), so this can't be saved. View that row: `,
    yahooLookupRunning: (symbol) => `Looking up ${symbol}…`,
    yahooLookupFilled: (symbol, source) => `Filled in ${symbol}'s company name and market from ${source === 'nasdaq_trader' ? "NASDAQ Trader's official listing" : 'Yahoo'} - check them, then add`,
    yahooLookupNotFound: (symbol) => `${symbol} not found on NASDAQ Trader's listing or Yahoo - fill in the company name and market manually`,
    addingStock: (symbol) => `Adding ${symbol} and looking up its company info and CUSIP (takes a few seconds)…`,
    addedStock: (symbol) => `Added ${symbol}. View: `,
    addedAllFound: (symbol) => `Added ${symbol}; company info and CUSIP were both found. View: `,
    addedSomeMissing: (symbol) => `Added ${symbol}, but some data isn't available yet - reasons below. View: `,
    addedPending: (symbol) => `Added ${symbol}. The data sources are slow to answer; the lookup continues in the background - check the detail page shortly: `,
    afTitle: "Some data isn't available yet:",
    afDetailsLabel: 'Company info (sector, address, website, CEO, description): ',
    afDetailsNotFound: 'Yahoo Finance has no company profile for this stock yet. Common for new listings - Yahoo usually adds one within hours to days.',
    afCusipLabel: 'CUSIP: ',
    afCusipNoSource: 'no source had it - ',
    afCusipConflict: (cusip, source, symbol) => `found ${cusip} (from ${source}), but that number already belongs to ${symbol}. Two stocks can't share a CUSIP, so it wasn't written - someone needs to check which one is right.`,
    afError: (message) => `lookup failed (${message}) - the network or the source site may be having trouble.`,
    afReasons: {
      no_contact: () => 'SEC_EDGAR_CONTACT is not set, so SEC cannot be queried',
      no_cik: () => "this symbol isn't in SEC's company ticker list (foreign company, OTC stock, or too new to be listed)",
      skipped_non_common: () => "preferreds / notes / warrants / units have CUSIPs that SEC 13G filings don't carry, so SEC was skipped",
      skipped_multi_class: () => "this company has several share classes and its 13G filings don't say which, so SEC was skipped",
      no_13g: () => 'no 13G ownership filing about this company yet (newly listed, or no institution holds over 5%)',
      unusable_13g: () => "13G filings exist, but their issuer name or CUSIP couldn't be confirmed",
      conflicting_13g: () => "the 13G filings disagree on the CUSIP, so it can't be told which is right",
      no_13f: () => "this company's common stock isn't in recent 13F institutional holdings reports",
      ambiguous_13f: (detail) => `13F holdings reports show several possible CUSIPs${detail ? ` (${detail})` : ''}, so it can't be told which is right`,
      belongs_to_other: (detail) => {
        const [cusip, symbol] = String(detail || '').split(' ');
        return `the ${cusip} found is ${symbol}'s CUSIP (confirmed by SEC), not this stock's, so it wasn't used`;
      },
      not_found: () => 'symbol not found',
      name_mismatch: (detail) => `the security name on the page${detail ? ` (${detail})` : ''} doesn't match the company - probably the symbol's previous owner`,
      no_cusip_on_page: () => 'the page has no CUSIP',
      invalid_check_digit: () => 'the CUSIP found has a wrong check digit',
      cik_name_mismatch: (detail) => `the company SEC lists for this symbol${detail ? ` (${detail})` : ''} doesn't match the company name - possibly the symbol's previous owner - so SEC 13G filings weren't used`,
      padded_sedol: (detail) => `the value found${detail ? ` (${detail})` : ''} looks like "00 + SEDOL" (often used for a foreign ordinary share that has no CUSIP), not certainly a CUSIP, so it was skipped`,
      error: (detail) => `request failed (${detail})`,
    },
    afRetryAt: (time) => `It will be looked up again automatically at ${time}.`,
    afRetryAtDetail: (time) => `It will be looked up again automatically at ${time}; this page updates itself.`,
    afRetriesExhausted: 'Automatic retries are over and it is still missing; run scripts/fill-company-details.js or scripts/fill-cusip.js later.',
    editSectionHelp: 'Enter a stock symbol and click Lookup first, then fill in the new value.',
    renameSectionTitle: 'Update Company Name',
    renameCurrentName: 'Current Company Name',
    renameNewNameLabel: 'New Company Name',
    renameSubmitBtn: 'Update Name',
    symbolSectionTitle: 'Update Stock Symbol',
    symbolCurrentLabel: 'Current Stock Symbol',
    symbolNewLabel: 'New Stock Symbol',
    symbolSubmitBtn: 'Update Symbol',
    marketSectionTitle: 'Update Trading Market',
    marketCurrentLabel: 'Current Trading Market',
    marketNewLabel: 'New Trading Market',
    marketSubmitBtn: 'Update Market',
    lookupBtn: 'Lookup',
    clearBtn: 'Clear',
    stockNotFound: 'Stock symbol not found',
    loginTitle: 'Log In',
    loginHelp: 'Log in with your credentials below to access the platform.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    labelEmail: 'Email',
    labelPassword: 'Password',
    loginBtn: 'Sign In',
    loggingIn: 'Signing in...',
    loginInvalid: "Wrong email or password, or this account doesn't have access.",
    loginTooMany: 'Too many failed logins. Please try again in 15 minutes.',
    loginMissing: 'Please fill all values',
    loginNetworkError: "Can't reach the server. Please try again later.",
    loginExpired: 'Your session has expired. Please log in again.',
    loginHelpEmailOnly: 'Enter an email that has access to log in (no password needed for now).',
    loginInvalidEmailOnly: "This email doesn't have access.",
    loginTooManyEmailOnly: 'Too many failed logins. Please try again in 15 minutes.',
    loginMissingEmailOnly: 'Please enter your email.',
    logoutBtn: 'Log out',
    pageNotFound: 'Page Not Found',
    pageNotFoundHelp: "There is no page at this address. A stock's page is /<symbol>, e.g. /aapl.",
    renameSameName: 'New name is the same as the current name',
    symbolSameSymbol: 'New symbol is the same as the current symbol',
    marketSameMarket: 'New market is the same as the current market',
    updatedOk: (symbol) => `Updated ${symbol}. View: `,
    monitorTitle: 'Stock Monitor & Auto Update',
    monitorHelp: 'Preview (nothing is written to the database): checks the US stocks (not delisted) that have no CUSIP yet and lists what would change - listed or delisted, name, market, category, currency, and stocks that were renamed or moved to a new ticker. Stocks that already have a CUSIP are not checked. Nothing is changed or deleted.',
    monitorPreviewBtn: 'Preview (no writes)',
    monitorStopBtn: 'Stop',
    monitorExportBtn: (n) => `Export CSV (${n.toLocaleString()} stocks)`,
    monitorExportNoPreview: 'Run a preview first - the export lists every stock it shows.',
    monitorExportNone: 'The last preview listed no stocks to export.',
    monitorExportHint: 'Every stock the preview lists: id, symbol, name, status, changes, notes; for a rename or new ticker also the new symbol and name (not added, so no id yet) and CUSIP',
    monitorStarting: 'Starting…',
    monitorStopping: 'Stopping after the current row…',
    monitorAlreadyRunning: 'A check is already running',
    monitorFailed: (msg) => `Check failed: ${msg}`,
    monitorRunning: (apply, phase, done, total) => `${apply ? 'Checking & updating' : 'Previewing'}: ${({
      loading: 'step 1 of 4 - loading the stocks without a CUSIP',
      quotes: 'step 2 of 4 - fetching Yahoo quotes (last trade date, name, market)',
      otc: 'step 3 of 4 - confirming whether OTC stocks are still trading or delisted (asking OTC Markets about OTC stocks with no Yahoo trade for 120+ days)',
      updating: 'step 4 of 4 - comparing status, name, market and category per stock',
    })[phase] || phase}${total ? ` ${done.toLocaleString()}/${total.toLocaleString()}` : ''}…`,
    monitorStoppedNote: (r) => (r.stoppedDuringQuotes ? '[Stopped while fetching quotes - nothing was written] ' : `[Stopped${r.apply ? ' - changes made before the stop were written' : ''}] `),
    monitorSummary: (r, fields) => `Last ${r.apply ? 'check & update' : 'preview (nothing written)'} (${new Date(r.finishedAt).toLocaleString('en-US')}${r.trigger === 'schedule' ? ', scheduled' : ''}): checked ${r.counts.checked.toLocaleString()} stocks without a CUSIP - listed ${r.counts.listed.toLocaleString()}, delisted ${r.counts.delisted.toLocaleString()}, unknown ${r.counts.unknown.toLocaleString()}. `
      + `Field changes: ${Object.entries(r.fieldCounts).map(([f, n]) => `${fields[f] || f} ${n}`).join(', ') || 'none'}${r.apply ? ` (${r.counts.updated} rows updated${r.counts.errors ? `, ${r.counts.errors} failed` : ''})` : ''}. `
      + `${r.counts.added ? `Renamed / new ticker: ${r.counts.added} new stock${r.counts.added === 1 ? '' : 's'} ${r.apply ? 'added' : 'to add'} (old rows marked delisted). ` : ''}`
      + `CUSIP: ${r.apply ? `filled ${r.cusip.filled}/${r.cusip.tried}, ` : ''}${r.cusip.missing.toLocaleString()} still missing.`
      + `${r.listingAvailable ? '' : ' (warning: NASDAQ Trader listing unavailable)'}${r.failedQuotes ? ` (warning: ${r.failedQuotes} symbols failed on Yahoo - status left alone)` : ''}`,
    monitorThStatus: 'Status',
    monitorThChanges: 'Changes / Notes',
    monitorEmptyPreview: 'No preview yet - click "Preview (no writes)" to run one.',
    monitorFilledDetails: (fields) => `Company info filled: ${fields.join(', ')}`,
    monitorFilledCusip: (cusip) => `CUSIP filled: ${cusip}`,
    monitorFillError: (part, msg) => `${part === 'cusip' ? 'CUSIP' : 'Company info'} lookup failed: ${msg}`,
    monitorCusipConflict: (c) => `Found CUSIP ${c.cusip}, but ${c.stock_symbol} (id ${c.id}) already has it - not written, please check`,
    monitorStatus: { listed: 'Listed', delisted: 'Delisted', unknown: 'Unknown' },
    monitorFields: { company_name: 'Name', exchange: 'Market', isdelisted: 'Delisted', category: 'Category', currency: 'Currency', cusips: 'CUSIP' },
    monitorGroupChanges: 'Changed automatically',
    monitorGroupApplied: 'Updated automatically',
    monitorGroupFills: 'Filled in',
    monitorGroupReview: 'Needs attention (not changed)',
    monitorGroupErrors: 'Errors',
    monitorGroupApplyErrors: 'Not written / errors',
    monitorCatAll: 'All',
    monitorCatHint: 'Click a category to filter the results below; one stock can be in several categories.',
    monitorCatEmpty: 'Nothing in this category',
    monitorCats: {
      delist: 'Marked delisted',
      revive: 'Listed again',
      name: 'Name corrected',
      exchange: 'Market changed',
      category: 'Category changed',
      currency: 'Currency changed',
      recycled: 'Symbol used by another company',
      duplicate: 'Duplicate symbol',
      nameDiffers: 'Name differs',
      nameTaken: 'Name held by another row',
      nameFreed: 'Delisted row renamed (name freed)',
      tickerChanged: 'Symbol changed (old ticker delisted)',
      replacedBy: 'Renamed / new ticker (old row delisted)',
      newRow: 'New stock added',
      otherNote: 'Other notes',
      detailsUpdated: 'Company info updated',
      cusipUpdated: 'CUSIP updated',
      detailsFilled: 'Company info filled',
      cusipFilled: 'CUSIP filled',
      cusipConflict: 'CUSIP held by another stock',
      error: 'Write failed',
    },
    monitorNotes: {
      recycled: (name) => `The symbol now trades as another company (${name}) - left delisted`,
      duplicate: () => 'The symbol is trading, but another non-delisted row already has it - left delisted',
      nameDiffers: (name) => `The sources give a different name: ${name} (not changed automatically - please check)`,
      nameFreed: (id, name) => `This delisted row was named "${name}"; "(old)" was added so the listed row with this symbol (id ${id}) can use that name (the table doesn't allow two rows with the same symbol and name)`,
      nameTaken: (id, delisted, name) => `Should be renamed to "${name}", but another${delisted ? ' delisted' : ''} row with this symbol (id ${id}) already has that name (the table doesn't allow two rows with the same symbol and name) - nothing changed on this row, please check`,
      tickerChanged: (symbol, id, cusip) => `This security now trades as ${symbol} (id ${id}): this old-ticker row was marked delisted first, then CUSIP ${cusip} was written to the new row`,
      replacedBy: (symbol, name, kind, rest) => `${kind === 'renamed' ? 'The company was renamed' : 'It now trades under a new ticker'}: ${symbol} "${name}". This old row is marked delisted with its name unchanged; ${rest === 'a new row is added for it' ? 'a new stock is added for it' : `${symbol} already has a row, so none is added`}`,
      newRowFor: (id, symbol, name) => `New stock, replacing id ${id} (${symbol} "${name}", renamed or moved to a new ticker - marked delisted)`,
    },
  },
};
